import { createContext, useContext, useEffect, useState } from 'react';
import { loopConcurrencyCap } from './mediaPolicy';

/**
 * A tiny slot manager that caps how many card previews may play media at once.
 * Cards request a slot when they scroll into view and release it when they leave or
 * unmount; waiting cards are granted a slot first-come-first-served as slots free up.
 */
export interface SlotScheduler {
  readonly cap: number;
  /** Ask for a slot. `onGrant` runs (possibly synchronously) once granted. Returns a release/cancel fn. */
  request(id: string, onGrant: () => void): () => void;
  activeCount(): number;
}

export function createSlotScheduler(cap: number): SlotScheduler {
  const active = new Set<string>();
  const queue: Array<{ id: string; onGrant: () => void }> = [];

  const pump = () => {
    while (active.size < cap && queue.length > 0) {
      const next = queue.shift()!;
      active.add(next.id);
      next.onGrant();
    }
  };

  return {
    cap,
    request(id, onGrant) {
      const waiter = { id, onGrant };
      if (active.size < cap && !active.has(id)) {
        active.add(id);
        onGrant();
      } else {
        queue.push(waiter);
      }
      return () => {
        if (active.delete(id)) {
          pump();
        } else {
          const i = queue.indexOf(waiter);
          if (i >= 0) queue.splice(i, 1);
        }
      };
    },
    activeCount: () => active.size,
  };
}

const LoopSlotContext = createContext<SlotScheduler | null>(null);
export const LoopSlotProvider = LoopSlotContext.Provider;

let fallbackScheduler: SlotScheduler | null = null;
function defaultScheduler(): SlotScheduler {
  if (!fallbackScheduler) fallbackScheduler = createSlotScheduler(loopConcurrencyCap());
  return fallbackScheduler;
}

/** Returns true while this card holds a play slot. Requests one only while `wanted`. */
export function useLoopSlot(id: string, wanted: boolean): boolean {
  const scheduler = useContext(LoopSlotContext) ?? defaultScheduler();
  const [granted, setGranted] = useState(false);
  useEffect(() => {
    if (!wanted) return undefined;
    let alive = true;
    const release = scheduler.request(id, () => {
      if (alive) setGranted(true);
    });
    return () => {
      alive = false;
      release();
      setGranted(false);
    };
  }, [id, wanted, scheduler]);
  return wanted && granted;
}
